/* eslint-disable no-bitwise */
import { capitalize, sortBy, find } from 'lodash';
import { createSelector } from 'reselect';

import { getAllowedActionsFromSettings } from './settings';
import {
  ACTION_NAME_TO_DISPLAY,
  FIELDS_ACTIONS,
  LABELS_TO_TOOLTIP,
} from './actions.constants';

const calculateValueForAction = (allActions, res, { name, subtype }) => {
  const foundAction = find(
    allActions,
    (action) => action.name === name && action.subtype === subtype,
  );

  return res | (foundAction ? foundAction.value : 0);
};

const actionsToValue = (optionActions, allActions) => {
  return optionActions.reduce(
    calculateValueForAction.bind(null, allActions),
    0,
  );
};

const prepareAction = (action) => {
  const [name, subtype] = action.name.split('-');

  return { ...action, name, subtype: subtype || action.subtype };
};

const customlyGroupActionsForFields = (productActions) => {
  if (!productActions.length) {
    return productActions;
  }

  return FIELDS_ACTIONS.map(({ label, permissions, tooltip }) => ({
    label,
    value: actionsToValue(permissions, productActions.map(prepareAction)),
    tooltip,
  }));
};

export const getActionsForFields = createSelector(
  (state) => state.productActions.list,
  customlyGroupActionsForFields,
);

const byActionType = (res, { name, value, subtype = 'ALLOW' }) => {
  const displayName = ACTION_NAME_TO_DISPLAY[name] || name;
  const label = capitalize(subtype);
  const tooltip = LABELS_TO_TOOLTIP[subtype];

  res[displayName] = [...(res[displayName] || []), { label, value, tooltip }];

  return res;
};

const groupByActionType = (productActions) => {
  const actions = productActions.map(prepareAction);

  const initValue = { create: [], view: [], edit: [], delete: [] };
  const sorted = sortBy(actions, ['name', 'subtype']);

  return sorted.reduce(byActionType, initValue);
};

export const getActionsGroupedByActionType = createSelector(
  (state) => state.productActions.list,
  groupByActionType,
);

const matchAllowedActionsWithActionsList = (r, actions) => {
  const resource = { ...r };

  // adding this patch for chime, chime's allowed_actions are a string
  if (!Array.isArray(resource.allowed_actions)) {
    resource.allowed_actions = [resource.allowed_actions];
  }
  const allowedActions = [];

  resource.allowed_actions.forEach((action) => {
    const filteredActions = actions.filter((obj) => obj.name === action);

    allowedActions.push(...filteredActions);
  });

  return allowedActions;
};

const sortEntry = (entry) => {
  return { name: entry.name, list: sortBy(entry.list, ['name', 'subtype']) };
};

const reduceList = (resource) => {
  const initialActionsValues = { create: [], view: [], edit: [], delete: [] };
  const preparedActions = resource.list.map(prepareAction);

  return {
    name: resource.name,
    list: preparedActions.reduce(byActionType, initialActionsValues),
  };
};

const groupByResourceType = (state) => {
  const productActions = state.productActions.list;
  const productResources = state.productResources.list;
  const resourceList = productResources.map((item) => {
    return {
      name: item.name,
      list: matchAllowedActionsWithActionsList(item, productActions),
    };
  });
  const sortedResourceList = resourceList.map((item) => sortEntry(item));
  const reducedResourceList = sortedResourceList.map((item) =>
    reduceList(item),
  );

  return reducedResourceList;
};

export const getActionsGroupedByResource = createSelector(
  (state) => state,
  groupByResourceType,
);

const flatTypeValue = (res, { name, value }) => {
  return [...res, { name: ACTION_NAME_TO_DISPLAY[name] || name, value }];
};

export const getValuesActions = createSelector(
  (state) => state.productActions.list,
  (productActions) =>
    productActions.map(prepareAction).reduce(flatTypeValue, []),
);

const groupActionsToKeyValue = ({ productActions }) => {
  return productActions.list.reduce(
    (res, { value, name }) => ({ ...res, [name]: value }),
    {},
  );
};

export const getProductActionsOptions = createSelector(
  getAllowedActionsFromSettings,
  groupActionsToKeyValue,
  (allowedActions, productActions) =>
    allowedActions &&
    allowedActions.map((action) => ({
      label: ACTION_NAME_TO_DISPLAY[action] || action,
      value: productActions[action],
    })),
);
